import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation } from "react-router";
import { useAuth } from "@/auth/AuthProvider";
import { NextcloudConnect } from "@/components/NextcloudConnect";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { missingAuthConfig } from "@/config";
import { isEmbedded } from "@/embedded";
import { useInstanceTagline } from "@/instance";

export function LoginPage() {
  const { t } = useTranslation();
  const [loginError, setLoginError] = useState<string>();
  const tagline = useInstanceTagline();
  const { user, loading, login } = useAuth();
  const location = useLocation();
  const returnTo = (location.state as { from?: string } | null)?.from ?? "/";

  if (loading) return <p className="muted">{t("auth.loading")}</p>;
  if (user) return <Navigate to={returnTo} replace />;
  // Embedded: the connect card is the sign-in; there is no Authentik button.
  if (isEmbedded()) return <NextcloudConnect />;

  const title = tagline === undefined ? t("app.tagline") : tagline;

  return (
    <Card className="mx-auto max-w-md">
      {title !== "" && (
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
      )}
      <CardContent className="stack">
        {missingAuthConfig().length > 0 ? (
          <p className="alert alert--danger">
            {t("auth.missingConfig", { keys: missingAuthConfig().join(", ") })}
          </p>
        ) : (
          <div className="row">
            <Button
              onClick={() => {
                setLoginError(undefined);
                login(returnTo).catch((e) =>
                  setLoginError(e instanceof Error ? e.message : String(e)),
                );
              }}
            >
              {t("auth.login")}
            </Button>
          </div>
        )}
        {loginError && (
          <p className="alert alert--danger">{t("auth.failed", { reason: loginError })}</p>
        )}
      </CardContent>
    </Card>
  );
}
